import React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Localizer } from "@/main/react/Types";

/**
 * On-ramp Tier 2.4, the stance prompts (round 25 `D41`,
 * designs/00-inbox/2026-06-17-argument-participation-ux-audit-and-on-ramp-design.md).
 *
 * Three chips that give a reader a first half-sentence instead of an empty box: agree, disagree, and a
 * neutral "Add a missing point". Choosing one opens or focuses the composer with the matching prefill
 * ("I agree, because "); the missing-point chip carries no prefill and only focuses it.
 *
 * When the post defines its own stance titles they replace Agree/Disagree, each side falling back on
 * its own exactly as `DiscussionButtons` resolves them (a blank or whitespace-only title is no title).
 *
 * This module is deliberately free of the editor: the empty thought list renders these chips on the
 * eager discussion page, and importing anything tiptap here would drag the lazy editor chunk with it.
 */
export type StancePromptId = "agree" | "disagree" | "missing-point";

export type StancePrompt = {
    id: StancePromptId;
    label: string;
    /** What the composer starts with. Empty for the missing-point chip, which only focuses. */
    prefill: string;
};

export function buildStancePrompts(
    localizer: Localizer,
    agreementTitle?: string | null,
    disagreementTitle?: string | null,
): StancePrompt[] {
    const customAgree = agreementTitle?.trim();
    const customDisagree = disagreementTitle?.trim();
    return [
        {
            id: "agree",
            label: customAgree
                ? localizer("discussion.stancePrompt.custom", customAgree)
                : localizer("discussion.stancePrompt.agree"),
            prefill: customAgree
                ? localizer("discussion.stancePrompt.prefill.custom", customAgree)
                : localizer("discussion.stancePrompt.prefill.agree"),
        },
        {
            id: "disagree",
            label: customDisagree
                ? localizer("discussion.stancePrompt.custom", customDisagree)
                : localizer("discussion.stancePrompt.disagree"),
            prefill: customDisagree
                ? localizer("discussion.stancePrompt.prefill.custom", customDisagree)
                : localizer("discussion.stancePrompt.prefill.disagree"),
        },
        {
            id: "missing-point",
            label: localizer("discussion.stancePrompt.missingPoint"),
            prefill: "",
        },
    ];
}

/**
 * The chip row. Outline pills at `size="sm"`, which carries the coarse-pointer tap floor in the
 * variant table (round 20 `D1`), so the row needs no floor of its own. Wraps rather than scrolls: a
 * custom stance title can be long, and the longest translation of "Disagree, because…" is roughly twice
 * the English.
 */
export function StancePromptChips(props: {
    prompts: StancePrompt[];
    onChoose: (prompt: StancePrompt) => void;
    playwrightIdPrefix: string;
    className?: string;
}) {
    const { prompts, onChoose, playwrightIdPrefix, className } = props;
    return (
        <div className={cn("flex min-w-0 flex-wrap gap-2", className)} data-playwright-id={`${playwrightIdPrefix}-stance-prompts`}>
            {prompts.map((prompt) => (
                <Button
                    key={prompt.id}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="max-w-full whitespace-normal text-left"
                    data-playwright-id={`${playwrightIdPrefix}-stance-prompt-${prompt.id}`}
                    data-stance-prompt={prompt.id}
                    onClick={() => onChoose(prompt)}
                >
                    {prompt.label}
                </Button>
            ))}
        </div>
    );
}
